import React, { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const sessionId = params.get("session_id");
    const courseId = params.get("course_id");

    const [status, setStatus] = useState("polling");
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!sessionId) { setStatus("failed"); return; }
        let active = true;

        async function tryConfirm() {
            // Fallback: Stripe redirected us to success_url, so trust it and ask server to confirm.
            try {
                await api.post(`/checkout/confirm/${sessionId}`);
                if (active) setStatus("paid");
            } catch (e) {
                if (active) setStatus("pending");
            }
        }

        async function poll(n = 0) {
            if (!active) return;
            if (n >= 6) {
                // Polling hasn't confirmed via Stripe — try the trust-the-redirect fallback
                if (user) {
                    await tryConfirm();
                } else {
                    setStatus("pending");
                }
                return;
            }
            try {
                const r = await api.get(`/checkout/status/${sessionId}`);
                if (r.payment_status === "paid") { setStatus("paid"); return; }
                if (r.status === "expired") { setStatus("expired"); return; }
            } catch {}
            setAttempt(n + 1);
            setTimeout(() => poll(n + 1), 2000);
        }
        poll(0);
        return () => { active = false; };
    }, [sessionId, user]);

    return (
        <div className="min-h-screen bg-ink-900 text-white flex items-center justify-center px-6">
            <div className="max-w-md w-full text-center bg-ink-700 rounded-3xl border border-white/5 p-10 fade-up">
                {status === "polling" && (
                    <>
                        <Loader2 className="w-12 h-12 text-lime mx-auto animate-spin" />
                        <h1 className="font-display font-black text-3xl mt-5">Confirming payment…</h1>
                        <p className="text-sm text-gray-400 mt-2">Hang tight, this only takes a moment. Attempt {attempt + 1}/6.</p>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <CheckCircle2 className="w-14 h-14 text-lime mx-auto" />
                        <h1 className="font-display font-black text-4xl mt-5 tracking-tight">You&apos;re enrolled.</h1>
                        <p className="text-sm text-gray-400 mt-3">Welcome to the cohort. Let&apos;s get to work.</p>
                        <div className="mt-8 flex gap-3 justify-center">
                            <Link to={`/learn/${courseId}`} data-testid="start-learning" className="px-6 py-3 rounded-full bg-lime text-ink-900 font-bold hover:bg-lime-hover transition-all">
                                Start learning
                            </Link>
                            <Link to="/my-courses" className="px-6 py-3 rounded-full border border-white/15 font-semibold hover:bg-white/5 transition-colors">
                                My courses
                            </Link>
                        </div>
                    </>
                )}
                {status === "pending" && (
                    <>
                        <Loader2 className="w-12 h-12 text-grape mx-auto" />
                        <h1 className="font-display font-black text-3xl mt-5">Payment is processing…</h1>
                        <p className="text-sm text-gray-400 mt-3 max-w-sm mx-auto">
                            Stripe is still finalising your transaction. Your enrolment will appear in <em>My Courses</em> within a minute. We&apos;ll also email a receipt.
                        </p>
                        <div className="mt-8 flex gap-3 justify-center">
                            <Link to="/my-courses" className="px-6 py-3 rounded-full bg-lime text-ink-900 font-bold hover:bg-lime-hover transition-all">
                                Go to My Courses
                            </Link>
                            <button onClick={() => window.location.reload()} className="px-6 py-3 rounded-full border border-white/15 font-semibold hover:bg-white/5 transition-colors">
                                Check again
                            </button>
                        </div>
                    </>
                )}
                {(status === "failed" || status === "expired") && (
                    <>
                        <XCircle className="w-14 h-14 text-red-400 mx-auto" />
                        <h1 className="font-display font-black text-3xl mt-5">Payment {status === "expired" ? "expired" : "issue"}.</h1>
                        <p className="text-sm text-gray-400 mt-3">No charge was made. You can try again from the course page.</p>
                        <div className="mt-8">
                            <button onClick={() => navigate(courseId ? `/catalog/${courseId}` : "/catalog")} className="px-6 py-3 rounded-full bg-lime text-ink-900 font-bold">
                                Back to course
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
